import { useMemo } from 'react'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import { toZonedTime } from 'date-fns-tz'
import { addMinutes, endOfDay, isToday, startOfDay, subMinutes } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { TIMEZONES } from '../../../../../helpers/constants'
import DateField from '../../../../FormLegacy/FormFieldsV2/DateField'
import { mergeDateAndTime } from '../../../common/helpers'

import FormInput from './FormInput'

const DateInput = ({ label, timeStart, timeEnd, timezone, setFieldValue }) => {
  const { t } = useTranslation(['eventManagement'])
  const timezoneString = useMemo(() => timezone || TIMEZONES.CT, [timezone])

  const { minStartTime, maxStartTime, minEndTime, maxEndTime } = useMemo(() => {
    const selectedDate = new Date(timeStart)

    const minTime = isToday(selectedDate)
      ? toZonedTime(new Date(), timezoneString)
      : startOfDay(toZonedTime(selectedDate, timezoneString))
    const maxTime = endOfDay(minTime)

    return {
      minStartTime: minTime,
      maxStartTime: subMinutes(maxTime, 15),
      minEndTime: addMinutes(minTime, 15),
      maxEndTime: maxTime,
    }
  }, [timeStart, timezoneString])

  const handleDateChange = (selectedDate) => {
    const datePart = new Date(selectedDate)
    const startTimePart = new Date(timeStart)
    const endTimePart = new Date(timeEnd)
    const newStartTime = mergeDateAndTime(datePart, startTimePart)
    const newEndTime = mergeDateAndTime(datePart, endTimePart)

    setFieldValue('timeStart', newStartTime)
    setFieldValue('timeEnd', newEndTime)
  }

  const handleTimeChange = (name, value, start, end) => {
    const selectedTime = moment(value)

    if (name === 'timeStart') {
      if (selectedTime.isSameOrAfter(moment(end), 'minutes')) {
        setFieldValue('timeEnd', selectedTime.clone().add(15, 'minutes').toDate())
      }
      setFieldValue(name, selectedTime.toDate())
    }
    if (name === 'timeEnd') {
      if (selectedTime.isSameOrBefore(moment(start), 'minutes')) {
        setFieldValue('timeStart', selectedTime.clone().subtract(15, 'minutes').toDate())
      }
      setFieldValue(name, selectedTime.toDate())
    }

    resetReminders()
  }

  const resetReminders = () => {
    setFieldValue('automatedRemindersToggle', false)
    setFieldValue('remindAll', false)
    setFieldValue('remindAllValues', [''])
    setFieldValue('remindRsvp', false)
    setFieldValue('remindRsvpValues', [''])
  }

  return (
    <div className="columns">
      <div className="column is-8">
        <FormInput label={label} name="date">
          <DateField
            popperPlacement="bottom-start"
            name="date"
            minDate={moment().toDate()}
            value={timeStart}
            onChange={(e) => handleDateChange(e.target.value)}
          />
        </FormInput>
      </div>
      <div className="column is-2">
        <FormInput label={t('eventManagement:common.startsLabel')} name="timeStart">
          <DateField
            popperPlacement="bottom-start"
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            dateFormat="h:mm aa"
            minTime={minStartTime}
            maxTime={maxStartTime}
            name="timeStart"
            value={timeStart}
            onChange={(e) => {
              handleTimeChange('timeStart', e.target.value, timeStart, timeEnd)
            }}
          />
        </FormInput>
      </div>
      <div className="column is-2">
        <FormInput label={t('eventManagement:common.endsLabel')} name="timeEnd">
          <DateField
            popperPlacement="bottom-start"
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            dateFormat="h:mm aa"
            minTime={minEndTime}
            maxTime={maxEndTime}
            name="timeEnd"
            value={timeEnd}
            onChange={(e) => {
              handleTimeChange('timeEnd', e.target.value, timeStart, timeEnd)
            }}
          />
        </FormInput>
      </div>
    </div>
  )
}

DateInput.propTypes = {
  label: PropTypes.string.isRequired,
  timeStart: PropTypes.object,
  timeEnd: PropTypes.object,
  timezone: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
}

export default DateInput
